import styled from '@emotion/styled';
import css from '@styled-system/css';
import {
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  ResponsiveValue,
  space,
  SpaceProps,
} from 'styled-system';

import BaseBox from '../../internal/components/BaseBox';
import { screenMediaQueries } from '../../internal/styles/screenMediaQueries';
import { Spaces } from '../../themes/cobalt/types';
import { DeprecatedTheme, Maybe } from '../../themes/types';
import Col from '../Col';

type ColGapCssProps = {
  paddingLeft: number;
  paddingRight: number;
};

type GapCssProps =
  | Record<string, ColGapCssProps>
  | {
      marginLeft: number;
      marginRight: number;
    };
const toHalfGap = (gap: Maybe<Spaces>, theme: DeprecatedTheme): number => {
  if (gap === null || gap === undefined) {
    return 0;
  }
  const halfGap = theme.space[gap];
  return typeof halfGap === 'number' ? halfGap : 0;
};

const calculateGapCssProps = (
  gap: Maybe<Spaces>,
  theme: DeprecatedTheme
): GapCssProps => {
  const halfGap = toHalfGap(gap, theme);
  return {
    marginLeft: -halfGap,
    marginRight: -halfGap,
    [`> ${Col}`]: {
      paddingLeft: halfGap,
      paddingRight: halfGap,
    },
  };
};

const calculateResponsiveHalfGapProps = (
  gap: Maybe<ResponsiveValue<Spaces>>,
  theme: DeprecatedTheme
) => {
  if (gap && typeof gap === 'object') {
    const result = {};
    Object.keys(gap).forEach((breakpoint) => {
      const cssProps = calculateGapCssProps(gap[breakpoint], theme);
      if (breakpoint === '_') {
        return Object.assign(result, cssProps);
      }
      return Object.assign(result, {
        [breakpoint]: cssProps,
      });
    });
    return result;
  }
  return calculateGapCssProps(gap, theme);
};

export interface FlexRowProps extends LayoutProps, FlexboxProps, SpaceProps {
  gap?: ResponsiveValue<Spaces>;
  theme?: DeprecatedTheme;
  display?: string;
}

export const FlexRow = styled(BaseBox)<FlexRowProps>(
  layout,
  space,
  flexbox,
  ({ gap, theme }) =>
    screenMediaQueries(calculateResponsiveHalfGapProps(gap, theme)),
  ({ display }) => ({
    display: display === 'inline-flex' ? display : 'flex',
  }),
  css({
    flexDirection: 'row',
  })
);

export default FlexRow;
